import * as THREE from "three";

type TransitionMaterialState = {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
  appliedOpacity: number;
  appliedTransparent: boolean;
  appliedDepthWrite: boolean;
};

const OPACITY_EPSILON = 0.001;

function materiallyDifferent(left: number, right: number): boolean {
  return Math.abs(left - right) > OPACITY_EPSILON;
}

export function setSceneMaterialOpacity(
  root: THREE.Object3D,
  opacity: number,
): void {
  const clampedOpacity = THREE.MathUtils.clamp(opacity, 0, 1);
  const transitionActive = clampedOpacity < 0.999;
  root.traverse((object) => {
    const materialOwner = object as THREE.Object3D & {
      material?: THREE.Material | THREE.Material[];
    };
    if (!materialOwner.material) return;
    const materials = Array.isArray(materialOwner.material)
      ? materialOwner.material
      : [materialOwner.material];
    materials.forEach((material) => {
      let state = material.userData.transitionMaterialState as
        | TransitionMaterialState
        | undefined;
      if (!state) {
        state = {
          opacity: material.opacity,
          transparent: material.transparent,
          depthWrite: material.depthWrite,
          appliedOpacity: material.opacity,
          appliedTransparent: material.transparent,
          appliedDepthWrite: material.depthWrite,
        } satisfies TransitionMaterialState;
        material.userData.transitionMaterialState = state;
      } else if (
        materiallyDifferent(material.opacity, state.appliedOpacity) ||
        material.transparent !== state.appliedTransparent ||
        material.depthWrite !== state.appliedDepthWrite
      ) {
        // React Three Fiber and loaded-model effects can update a material while
        // the scene stays mounted (e.g. symbol fillOpacity). Treat the live
        // opacity as the new absolute baseline.
        //
        // During a fade we force transparent=true / depthWrite=false. Those
        // must NOT become the restored baseline, or objects stay in transparent
        // render mode after the fade even when opacity returns to 1.
        state.opacity = material.opacity;
        if (!transitionActive) {
          state.transparent = material.transparent;
          state.depthWrite = material.depthWrite;
        }
      }
      const nextOpacity = state.opacity * clampedOpacity;
      const nextTransparent = state.transparent || transitionActive;
      const nextDepthWrite = transitionActive ? false : state.depthWrite;
      if (
        materiallyDifferent(material.opacity, nextOpacity) ||
        material.transparent !== nextTransparent ||
        material.depthWrite !== nextDepthWrite
      ) {
        material.opacity = nextOpacity;
        material.transparent = nextTransparent;
        material.depthWrite = nextDepthWrite;
        material.needsUpdate = true;
      }
      state.appliedOpacity = nextOpacity;
      state.appliedTransparent = nextTransparent;
      state.appliedDepthWrite = nextDepthWrite;
    });
  });
}
